import {
  type Asset,
  BRAND_COLOR_ROLE_LABELS,
  BRAND_LOGO_LABELS,
  BRAND_LOGO_VARIANTS,
  BRAND_RADIUS_LABELS,
  BRAND_SPACING_KEYS,
  BRAND_STYLES,
  type BrandColor,
  type BrandColorRole,
  type BrandKit,
  type BrandLogoVariant,
  type BrandTextStyle,
  type BrandTokens,
  brandContrastChecks,
  brandTokensSchema,
  closestWeight,
  formatBytes,
  type GoogleFont,
  getFontFamily,
  hasItalic,
  isRequiredColor,
  isRequiredTextStyle,
  mmToPx,
  nextTokenKey,
  PRINT_PROFILES,
  PRINT_PROVIDERS,
  resolvePrintProfile,
  WEIGHT_LABELS,
} from "@cva/shared";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { produce } from "immer";
import {
  AlertTriangle,
  Check,
  CloudOff,
  Copy,
  Download,
  ImagePlus,
  Loader2,
  Palette,
  Plus,
  Printer,
  Search,
  Star,
  Trash2,
  Type,
  Upload,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BrandImageColors } from "../components/brand-image-colors";
import { MediaPickerDialog } from "../components/media-picker";
import { Button } from "../components/ui/button";
import { Badge, Card, CardHeader, EmptyState, PageHeader, Skeleton } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm";
import { ContextMenu } from "../components/ui/context-menu";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import { DropdownItem, DropdownSeparator } from "../components/ui/dropdown";
import { Field, Input, Select } from "../components/ui/input";
import { FontPicker } from "../editor/FontPicker";
import { NumberField } from "../editor/ui";
import { client, orpc } from "../lib/api";
import { useOrgFonts } from "../lib/fonts";
import { useOrgId, useOrgRole } from "../lib/org";
import { cn, errorMessage } from "../lib/utils";

/**
 * Brand kit management (docs/06-brand-kit.md): colours with CMYK values,
 * text styles, fonts, logos, graphics, spacing and the default print profile.
 * Changes to tokens are saved automatically and reach all linked documents.
 */

const CHECKER = "repeating-conic-gradient(#e4e4e7 0% 25%, #fff 0% 50%) 50% / 10px 10px";

export function BrandPage() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { kit?: string };
  const { can } = useOrgRole(orgId);
  const canEdit = can("brand.update");
  const kits = useQuery(orpc.brand.list.queryOptions({ input: { orgId } }));
  const org = useQuery(orpc.orgs.get.queryOptions({ input: { orgId } }));
  const kitId =
    kits.data?.find((kit) => kit.id === search.kit)?.id ??
    kits.data?.find((kit) => kit.isDefault)?.id;
  const kit = useQuery({
    ...orpc.brand.get.queryOptions({ input: { orgId, kitId: kitId ?? "" } }),
    enabled: Boolean(kitId),
  });
  // Organizations start without a kit; the first one becomes the default kit.
  const noKit = kits.data?.length === 0;
  const [creating, setCreating] = useState(false);
  useOrgFonts(orgId);

  const selectKit = (id: string) =>
    void navigate({ to: "/o/$orgId/brand", params: { orgId }, search: { kit: id } });

  return (
    <TooltipPrimitive.Provider>
      <PageHeader
        title="Marke"
        description="Farben, Schriften, Logos und Druckprofil der Organisation. Verknüpfte Dateien übernehmen Änderungen automatisch."
        actions={
          <>
            {kits.data && kits.data.length > 1 ? (
              <Select
                value={kitId ?? ""}
                onChange={(event) => selectKit(event.target.value)}
                className="w-56"
                aria-label="Brand Kit wählen"
              >
                {kits.data.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                    {entry.isDefault ? " (Standard)" : ""}
                  </option>
                ))}
              </Select>
            ) : null}
            {canEdit && kits.data && !noKit ? (
              <Button variant="secondary" onClick={() => setCreating(true)}>
                <Plus /> Weiteres Brand Kit
              </Button>
            ) : null}
          </>
        }
      />
      {noKit ? (
        <div className="space-y-6">
          <EmptyState
            icon={<Palette />}
            title="Noch kein Brand Kit"
            description={
              canEdit
                ? "Ein Brand Kit bündelt Farben, Schriften, Logos und das Druckprofil deiner Marke. Neue Dateien übernehmen es, und Änderungen kommen automatisch in allen verknüpften Dateien an. Das erste Kit wird zum Standard-Kit."
                : "Ein Brand Kit bündelt Farben, Schriften, Logos und das Druckprofil der Marke. Anlegen können es Owner und Admins."
            }
            action={
              canEdit ? (
                <Button onClick={() => setCreating(true)}>
                  <Plus /> Brand Kit erstellen
                </Button>
              ) : null
            }
          />
          {/* Fonts belong to the organization, not to a kit. */}
          <fieldset disabled={!canEdit} className="min-w-0">
            <FontsSection orgId={orgId} canEdit={canEdit} />
          </fieldset>
        </div>
      ) : kit.data ? (
        <KitEditor
          key={kit.data.id}
          orgId={orgId}
          kit={kit.data}
          canEdit={canEdit}
          isOnlyKit={kits.data?.length === 1}
        />
      ) : (
        <div className="space-y-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-80" />
        </div>
      )}
      {creating && (noKit || kit.data) ? (
        <NewKitDialog
          orgId={orgId}
          source={noKit ? null : (kit.data ?? null)}
          suggestedName={noKit ? (org.data?.name ?? "") : ""}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            selectKit(id);
          }}
        />
      ) : null}
    </TooltipPrimitive.Provider>
  );
}

function NewKitDialog({
  orgId,
  source,
  suggestedName,
  onClose,
  onCreated,
}: {
  orgId: string;
  /** Kit to copy from; null for the organization's first kit. */
  source: BrandKit | null;
  suggestedName: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(suggestedName);
  const [copy, setCopy] = useState(true);
  const [style, setStyle] = useState<string | null>(null);
  const create = useMutation(
    orpc.brand.create.mutationOptions({
      onSuccess: async (kit) => {
        await queryClient.invalidateQueries({ queryKey: orpc.brand.key() });
        onCreated(kit.id);
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={source ? "Weiteres Brand Kit" : "Brand Kit erstellen"}
        description={
          source
            ? "Zum Beispiel für eine Submarke. Projekte können in ihren Einstellungen ein anderes Kit als das Standard-Kit nutzen."
            : "Das erste Kit wird zum Standard-Kit: Alle Projekte nutzen es, solange sie kein anderes Kit festlegen. Farben, Schriften und Logos passt du danach an."
        }
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const copying = Boolean(source && copy);
            create.mutate({
              orgId,
              name: name.trim(),
              copyFrom: copying ? source?.id : undefined,
              style: !copying && style ? style : undefined,
            });
          }}
          className="space-y-4"
        >
          <Field label="Name">
            <Input
              autoFocus
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={source ? "z. B. Sommerkampagne" : "z. B. Name deiner Marke"}
            />
          </Field>
          {source ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={copy}
                onChange={(event) => setCopy(event.target.checked)}
                className="size-4 accent-brand-600"
              />
              Farben, Schriften und Logos von „{source.name}“ übernehmen
            </label>
          ) : null}
          {!source || !copy ? <StyleChoice value={style} onChange={setStyle} /> : null}
          <DialogFooter>
            <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
              Anlegen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Starting point of a new kit: neutral defaults or one of the design styles. */
function StyleChoice({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (style: string | null) => void;
}) {
  const options = [
    {
      id: null,
      label: "Neutral",
      description: "Blau, Inter – alles selbst festlegen",
      colors: ["#2563eb", "#f59e0b", "#0f172a"],
      font: "Inter",
    },
    ...BRAND_STYLES.map((style) => ({
      id: style.id,
      label: style.label,
      description: style.description,
      colors: [style.colors.primary, style.colors.accent, style.colors.secondary],
      font: style.heading.fontFamily,
    })),
  ];
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-zinc-700">Startpunkt</p>
      <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto pr-1">
        {options.map((option) => (
          <button
            key={option.id ?? "neutral"}
            type="button"
            onClick={() => onChange(option.id)}
            className={cn(
              "rounded-lg border px-3 py-2 text-left transition",
              value === option.id
                ? "border-brand-500 bg-brand-50 ring-2 ring-brand-500/20"
                : "border-zinc-200 hover:border-zinc-300",
            )}
          >
            <span className="flex items-center gap-2">
              <span
                className="text-lg leading-none text-zinc-900"
                style={{ fontFamily: option.font }}
              >
                Aa
              </span>
              <span className="flex -space-x-1">
                {option.colors.map((color) => (
                  <span
                    key={color}
                    className="size-3.5 rounded-full ring-2 ring-white"
                    style={{ background: color }}
                  />
                ))}
              </span>
            </span>
            <span className="mt-1 block text-sm font-medium text-zinc-900">{option.label}</span>
            <span className="block text-xs text-zinc-500">{option.description}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-zinc-500">
        Farben und Schriften lassen sich danach jederzeit ändern.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kit editor with autosave
// ---------------------------------------------------------------------------

type SaveState = "saved" | "dirty" | "saving" | "invalid" | "error";

function useKitSaver(orgId: string, kit: BrandKit) {
  const queryClient = useQueryClient();
  const [tokens, setTokens] = useState<BrandTokens>(kit.tokens);
  const [state, setState] = useState<SaveState>("saved");
  const [problem, setProblem] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<BrandTokens | null>(null);

  const save = async () => {
    const next = pending.current;
    if (!next) return;
    const parsed = brandTokensSchema.safeParse(next);
    if (!parsed.success) {
      setState("invalid");
      setProblem(parsed.error.issues[0]?.message ?? "Ungültige Eingabe");
      return;
    }
    pending.current = null;
    setState("saving");
    try {
      const saved = await client.brand.update({ orgId, kitId: kit.id, tokens: parsed.data });
      queryClient.setQueryData(orpc.brand.get.queryKey({ input: { orgId, kitId: kit.id } }), saved);
      void queryClient.invalidateQueries({ queryKey: orpc.brand.list.key() });
      void queryClient.invalidateQueries({ queryKey: orpc.brand.effective.key() });
      void queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
      setProblem(null);
      setState(pending.current ? "dirty" : "saved");
    } catch (error) {
      pending.current = pending.current ?? next;
      setState("error");
      toast.error(`Speichern fehlgeschlagen: ${errorMessage(error)}`);
    }
  };

  const update = (recipe: (draft: BrandTokens) => void) => {
    setTokens((current) => {
      const next = produce(current, recipe);
      pending.current = next;
      return next;
    });
    setState("dirty");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void save(), 700);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: flush once on unmount
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      if (pending.current) void save();
    },
    [],
  );

  return { tokens, update, state, problem };
}

function SaveBadge({ state, problem }: { state: SaveState; problem: string | null }) {
  const map: Record<SaveState, { icon: ReactNode; label: string; className: string }> = {
    saved: {
      icon: <Check className="size-3.5" />,
      label: "Gespeichert",
      className: "text-zinc-500",
    },
    dirty: {
      icon: <Loader2 className="size-3.5" />,
      label: "Ungespeichert",
      className: "text-zinc-500",
    },
    saving: {
      icon: <Loader2 className="size-3.5 animate-spin" />,
      label: "Speichert …",
      className: "text-zinc-500",
    },
    invalid: {
      icon: <AlertTriangle className="size-3.5" />,
      label: problem ?? "Bitte Eingaben prüfen",
      className: "text-amber-600",
    },
    error: {
      icon: <CloudOff className="size-3.5" />,
      label: "Nicht gespeichert",
      className: "text-red-600",
    },
  };
  const entry = map[state];
  return (
    <span className={cn("inline-flex items-center gap-1 text-xs", entry.className)}>
      {entry.icon}
      {entry.label}
    </span>
  );
}

function KitEditor({
  orgId,
  kit,
  canEdit,
  isOnlyKit,
}: {
  orgId: string;
  kit: BrandKit;
  canEdit: boolean;
  isOnlyKit: boolean;
}) {
  const { tokens, update, state, problem } = useKitSaver(orgId, kit);
  return (
    <div className="space-y-6">
      <KitHeader
        orgId={orgId}
        kit={kit}
        canEdit={canEdit}
        isOnlyKit={isOnlyKit}
        saveBadge={<SaveBadge state={state} problem={problem} />}
      />
      <fieldset disabled={!canEdit} className="min-w-0 space-y-6">
        <ColorsSection orgId={orgId} tokens={tokens} update={update} canEdit={canEdit} />
        {canEdit ? (
          <BrandImageColors orgId={orgId} kit={kit} tokens={tokens} update={update} />
        ) : null}
        <TypographySection tokens={tokens} update={update} canEdit={canEdit} />
        <FontsSection orgId={orgId} canEdit={canEdit} />
        <LogosSection orgId={orgId} kit={kit} canEdit={canEdit} />
        <GraphicsSection orgId={orgId} kit={kit} canEdit={canEdit} />
        <SpacingSection tokens={tokens} update={update} />
        <PrintSection tokens={tokens} update={update} />
      </fieldset>
      {!canEdit ? (
        <p className="text-sm text-zinc-500">Nur Owner und Admins können das Brand Kit ändern.</p>
      ) : null}
    </div>
  );
}

function useKitMutation(orgId: string, kitId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.brand.update.mutationOptions({
      onSuccess: (saved) => {
        queryClient.setQueryData(orpc.brand.get.queryKey({ input: { orgId, kitId } }), saved);
        void queryClient.invalidateQueries({ queryKey: orpc.brand.list.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.brand.effective.key() });
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
}

function KitHeader({
  orgId,
  kit,
  canEdit,
  isOnlyKit,
  saveBadge,
}: {
  orgId: string;
  kit: BrandKit;
  canEdit: boolean;
  /** The default kit can only be deleted while it is the last one. */
  isOnlyKit: boolean;
  saveBadge: ReactNode;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useKitMutation(orgId, kit.id);
  const [name, setName] = useState(kit.name);
  const [deleting, setDeleting] = useState(false);
  return (
    <Card className="flex flex-wrap items-center gap-4 px-5 py-4">
      <div className="flex -space-x-1.5">
        {kit.previewColors.map((color, index) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: static preview
            key={index}
            className="size-8 rounded-full ring-2 ring-white"
            style={{ background: color }}
          />
        ))}
      </div>
      <div className="min-w-0 flex-1">
        {canEdit ? (
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => {
              const trimmed = name.trim();
              if (trimmed && trimmed !== kit.name) {
                mutation.mutate({ orgId, kitId: kit.id, name: trimmed });
              } else setName(kit.name);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") (event.currentTarget as HTMLInputElement).blur();
            }}
            aria-label="Name des Brand Kits"
            className="w-full max-w-md rounded-md border border-transparent px-1 text-lg font-semibold hover:border-zinc-200 focus:border-brand-500 focus:outline-none"
          />
        ) : (
          <p className="px-1 text-lg font-semibold">{kit.name}</p>
        )}
        <p className="px-1 text-sm text-zinc-500">
          {kit.isDefault ? "Standard-Kit · " : ""}
          {kit.projectCount === 1 ? "1 Projekt" : `${kit.projectCount} Projekte`} nutzen dieses Kit
        </p>
      </div>
      {saveBadge}
      {kit.isDefault ? (
        <Badge tone="brand">
          <Star className="mr-1 size-3" /> Standard
        </Badge>
      ) : canEdit ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => mutation.mutate({ orgId, kitId: kit.id, isDefault: true })}
        >
          <Star /> Als Standard festlegen
        </Button>
      ) : null}
      {canEdit && (!kit.isDefault || isOnlyKit) ? (
        <Button variant="ghost" size="sm" onClick={() => setDeleting(true)} aria-label="Löschen">
          <Trash2 />
        </Button>
      ) : null}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        danger
        title={`„${kit.name}“ löschen?`}
        description={
          kit.isDefault
            ? "Die Organisation hat danach kein Brand Kit mehr. Verknüpfte Dateien behalten ihre aktuellen Farben und Schriften, neue Dateien starten neutral."
            : "Projekte mit diesem Kit nutzen danach das Standard-Kit. Verknüpfte Dateien übernehmen dessen Farben und Schriften."
        }
        confirmLabel="Löschen"
        onConfirm={async () => {
          await client.brand.remove({ orgId, kitId: kit.id });
          await queryClient.invalidateQueries({ queryKey: orpc.brand.key() });
          await queryClient.invalidateQueries({ queryKey: orpc.projects.key() });
          await navigate({ to: "/o/$orgId/brand", params: { orgId }, search: {} });
        }}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

type Update = (recipe: (draft: BrandTokens) => void) => void;

function kitProfile(tokens: BrandTokens) {
  return resolvePrintProfile({
    provider: tokens.print.provider ?? undefined,
    paper: tokens.print.paper ?? undefined,
    profile: tokens.print.profile ?? undefined,
  });
}

function ColorsSection({
  orgId,
  tokens,
  update,
  canEdit,
}: {
  orgId: string;
  tokens: BrandTokens;
  update: Update;
  canEdit: boolean;
}) {
  const profile = kitProfile(tokens);
  const checks = brandContrastChecks(tokens);
  const name = (key: string) => tokens.colors.find((color) => color.key === key)?.name ?? key;
  const hex = (key: string) => tokens.colors.find((color) => color.key === key)?.color ?? "#000";
  return (
    <Card>
      <CardHeader
        title="Farben"
        description={`Bildschirmfarbe (RGB) und optional der exakte CMYK-Wert aus dem Styleguide. Ohne CMYK-Wert wird über ${profile.label} umgerechnet.`}
        actions={
          canEdit ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={tokens.colors.length >= 60}
              onClick={() =>
                update((draft) => {
                  draft.colors.push({
                    key: nextTokenKey(draft.colors, "farbe"),
                    name: "Neue Farbe",
                    color: "#7c3aed",
                    cmyk: null,
                    pantone: null,
                  });
                })
              }
            >
              <Plus /> Farbe
            </Button>
          ) : null
        }
      />
      <div className="grid gap-4 p-5 sm:grid-cols-2 xl:grid-cols-3">
        {tokens.colors.map((color, index) => (
          <ColorCard
            key={color.key}
            orgId={orgId}
            color={color}
            profile={profile.id}
            canEdit={canEdit}
            onChange={(patch) =>
              update((draft) => {
                Object.assign(draft.colors[index]!, patch);
              })
            }
            onRemove={
              isRequiredColor(color.key)
                ? undefined
                : () =>
                    update((draft) => {
                      draft.colors.splice(index, 1);
                      for (const style of draft.textStyles) {
                        if (style.color === color.key) style.color = null;
                      }
                    })
            }
          />
        ))}
      </div>
      <div className="border-t border-zinc-100 px-5 py-4">
        <p className="mb-2 text-sm font-medium text-zinc-700">Kontrast (WCAG)</p>
        <div className="flex flex-wrap gap-2">
          {checks.map((check) => (
            <div
              key={`${check.foreground}-${check.background}`}
              className="flex items-center gap-2 rounded-lg border border-zinc-200 py-1.5 pr-2.5 pl-1.5 text-sm"
              title={`${name(check.foreground)} auf ${name(check.background)}`}
            >
              <span
                className="flex h-7 w-10 items-center justify-center rounded-md text-sm font-semibold ring-1 ring-black/10 ring-inset"
                style={{ background: hex(check.background), color: hex(check.foreground) }}
              >
                Aa
              </span>
              <span className="text-zinc-600">
                {name(check.foreground)} / {name(check.background)}
              </span>
              <span className="font-mono text-xs tabular-nums">{check.ratio.toFixed(1)}:1</span>
              <Badge
                tone={
                  check.level === "AAA" || check.level === "AA"
                    ? "success"
                    : check.level === "AA groß"
                      ? "warning"
                      : "danger"
                }
              >
                {check.level}
              </Badge>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          AA (4,5:1) reicht für Fließtext, „AA groß“ (3:1) nur für große Überschriften.
        </p>
      </div>
    </Card>
  );
}

function CmykInputs({
  value,
  onChange,
}: {
  value: [number, number, number, number] | null;
  onChange: (value: [number, number, number, number] | null) => void;
}) {
  const [draft, setDraft] = useState<string[]>(value ? value.map(String) : ["", "", "", ""]);
  useEffect(() => setDraft(value ? value.map(String) : ["", "", "", ""]), [value]);
  const commit = (next: string[]) => {
    if (next.every((entry) => entry.trim() === "")) {
      onChange(null);
      return;
    }
    const numbers = next.map((entry) =>
      Math.max(0, Math.min(100, Math.round(Number(entry.replace(",", ".")) || 0))),
    ) as [number, number, number, number];
    onChange(numbers);
  };
  return (
    <div className="grid grid-cols-4 gap-1">
      {(["C", "M", "Y", "K"] as const).map((channel, index) => (
        <label
          key={channel}
          className="flex h-8 items-center rounded-md border border-zinc-200 bg-white px-1.5 text-sm focus-within:border-brand-500"
        >
          <span className="text-xs text-zinc-400">{channel}</span>
          <input
            inputMode="numeric"
            value={draft[index]}
            placeholder="–"
            onChange={(event) => {
              const next = [...draft];
              next[index] = event.target.value.replace(/[^\d.,]/g, "");
              setDraft(next);
            }}
            onBlur={() => commit(draft)}
            onKeyDown={(event) => {
              if (event.key === "Enter") (event.currentTarget as HTMLInputElement).blur();
            }}
            className="w-full bg-transparent text-right tabular-nums outline-none"
          />
        </label>
      ))}
    </div>
  );
}

function ColorCard({
  orgId,
  color,
  profile,
  canEdit,
  onChange,
  onRemove,
}: {
  orgId: string;
  color: BrandColor;
  profile: string;
  canEdit: boolean;
  onChange: (patch: Partial<BrandColor>) => void;
  onRemove?: () => void;
}) {
  const [hex, setHex] = useState(color.color);
  useEffect(() => setHex(color.color), [color.color]);
  const report = useQuery({
    ...orpc.print.colors.queryOptions({
      input: {
        orgId,
        profile,
        colors: [color.color],
        swatches: color.cmyk ? [{ color: color.color, cmyk: color.cmyk }] : [],
      },
    }),
    staleTime: Number.POSITIVE_INFINITY,
    select: (reports) => reports[0],
  });
  const exactProof = useQuery({
    ...orpc.print.proof.queryOptions({
      input: { orgId, profile, cmyk: color.cmyk ?? [0, 0, 0, 0] },
    }),
    enabled: Boolean(color.cmyk),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const role = isRequiredColor(color.key)
    ? BRAND_COLOR_ROLE_LABELS[color.key as BrandColorRole]
    : null;
  return (
    <ContextMenu
      menu={
        <>
          <DropdownItem
            onSelect={() =>
              void navigator.clipboard
                .writeText(color.color.toUpperCase())
                .then(() => toast.success(`${color.color.toUpperCase()} kopiert`))
            }
          >
            <Copy /> HEX kopieren
          </DropdownItem>
          {color.cmyk ? (
            <DropdownItem
              onSelect={() =>
                void navigator.clipboard
                  .writeText(color.cmyk!.join(" / "))
                  .then(() => toast.success("CMYK-Werte kopiert"))
              }
            >
              <Copy /> CMYK kopieren
            </DropdownItem>
          ) : null}
          {onRemove && canEdit ? (
            <>
              <DropdownSeparator />
              <DropdownItem danger onSelect={onRemove}>
                <Trash2 /> Farbe entfernen
              </DropdownItem>
            </>
          ) : null}
        </>
      }
    >
      <div className="overflow-hidden rounded-xl border border-zinc-200">
        <label
          className={cn("relative block h-20", canEdit && "cursor-pointer")}
          style={{ background: color.color }}
          title={canEdit ? "Farbe wählen" : undefined}
        >
          <input
            type="color"
            value={color.color}
            disabled={!canEdit}
            onChange={(event) => onChange({ color: event.target.value.toLowerCase() })}
            className="absolute inset-0 size-full cursor-pointer opacity-0"
          />
          {role ? (
            <span className="absolute top-2 left-2 rounded bg-white/85 px-1.5 py-0.5 text-[11px] font-medium text-zinc-700">
              {role}
            </span>
          ) : null}
          {onRemove && canEdit ? (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                onRemove();
              }}
              title="Farbe entfernen"
              className="absolute top-2 right-2 rounded bg-white/85 p-1 text-zinc-600 hover:text-red-600"
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </label>
        <div className="space-y-2 p-3">
          <Input
            value={color.name}
            onChange={(event) => onChange({ name: event.target.value })}
            aria-label="Name"
            className="h-8"
          />
          <div className="flex gap-2">
            <Input
              value={hex}
              onChange={(event) => setHex(event.target.value)}
              onBlur={() => {
                const normalized = hex.startsWith("#") ? hex : `#${hex}`;
                if (/^#[0-9a-f]{6}$/i.test(normalized))
                  onChange({ color: normalized.toLowerCase() });
                else setHex(color.color);
              }}
              aria-label="Hex-Wert"
              className="h-8 w-24 shrink-0 font-mono text-xs uppercase"
            />
            <Input
              value={color.pantone ?? ""}
              onChange={(event) => onChange({ pantone: event.target.value || null })}
              placeholder="Pantone (Info)"
              aria-label="Pantone"
              className="h-8 min-w-0 text-xs"
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between text-xs text-zinc-500">
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <Printer className="size-3" /> CMYK
              </span>
              {canEdit && !color.cmyk && report.data ? (
                <button
                  type="button"
                  className="font-medium whitespace-nowrap text-brand-700 hover:underline"
                  onClick={() =>
                    onChange({
                      cmyk: report.data!.cmyk.map((value) => Math.round(value)) as [
                        number,
                        number,
                        number,
                        number,
                      ],
                    })
                  }
                >
                  Vorschlag übernehmen
                </button>
              ) : null}
            </div>
            <CmykInputs value={color.cmyk} onChange={(cmyk) => onChange({ cmyk })} />
            <p className="mt-1 text-xs text-zinc-500">
              {color.cmyk
                ? "Exakt festgelegt – landet unverändert im Druck-PDF."
                : report.data
                  ? `Aus Profil: ${report.data.cmyk.map((value) => Math.round(value)).join(" / ")}`
                  : "Wird berechnet …"}
            </p>
          </div>
          {!color.cmyk && report.data?.outOfGamut ? (
            <div className="flex items-center gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900">
              <AlertTriangle className="size-3.5 shrink-0" />
              <span className="flex-1">Im Druck blasser (ΔE {report.data.deltaE.toFixed(1)})</span>
              <span
                className="size-5 rounded ring-1 ring-black/10"
                style={{ background: color.color }}
              />
              →
              <span
                className="size-5 rounded ring-1 ring-black/10"
                style={{ background: report.data.proof }}
              />
            </div>
          ) : null}
          {color.cmyk && exactProof.data ? (
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              So sieht der CMYK-Wert gedruckt ungefähr aus:
              <span
                className="size-5 rounded ring-1 ring-black/10"
                style={{ background: exactProof.data.color, backgroundImage: undefined }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </ContextMenu>
  );
}

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

/** Size of a brand text style on an A5 flyer in pt (for orientation). */
function a5Points(size: number) {
  return Math.round(((size * mmToPx(148)) / 1080) * 0.75);
}

function TypographySection({
  tokens,
  update,
  canEdit,
}: {
  tokens: BrandTokens;
  update: Update;
  canEdit: boolean;
}) {
  return (
    <Card>
      <CardHeader
        title="Textstile"
        description="Größen gelten für ein 1080 px breites Format und werden für andere Formate proportional umgerechnet."
        actions={
          canEdit ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={tokens.textStyles.length >= 30}
              onClick={() =>
                update((draft) => {
                  draft.textStyles.push({
                    ...draft.textStyles.find((style) => style.key === "body")!,
                    key: nextTokenKey(draft.textStyles, "stil"),
                    name: "Neuer Stil",
                  });
                })
              }
            >
              <Plus /> Textstil
            </Button>
          ) : null
        }
      />
      <div className="divide-y divide-zinc-100">
        {tokens.textStyles.map((style, index) => (
          <TextStyleRow
            key={style.key}
            style={style}
            colors={tokens.colors}
            canEdit={canEdit}
            onChange={(patch) =>
              update((draft) => {
                Object.assign(draft.textStyles[index]!, patch);
              })
            }
            onRemove={
              isRequiredTextStyle(style.key)
                ? undefined
                : () =>
                    update((draft) => {
                      draft.textStyles.splice(index, 1);
                    })
            }
          />
        ))}
      </div>
    </Card>
  );
}

function TextStyleRow({
  style,
  colors,
  canEdit,
  onChange,
  onRemove,
}: {
  style: BrandTextStyle;
  colors: BrandColor[];
  canEdit: boolean;
  onChange: (patch: Partial<BrandTextStyle>) => void;
  onRemove?: () => void;
}) {
  const font = getFontFamily(style.fontFamily);
  const color = colors.find((entry) => entry.key === style.color)?.color ?? "#111827";
  const background = colors.find((entry) => entry.key === "background")?.color ?? "#ffffff";
  return (
    <div className="grid gap-4 px-5 py-4 lg:grid-cols-[1fr_380px]">
      <div className="min-w-0">
        <div className="mb-2 flex items-center gap-2">
          <Input
            value={style.name}
            onChange={(event) => onChange({ name: event.target.value })}
            aria-label="Name des Textstils"
            className="h-7 w-44 text-xs font-medium"
          />
          <span className="text-xs text-zinc-400">
            {style.fontSize} px im Post · {a5Points(style.fontSize)} pt auf A5
          </span>
          {onRemove && canEdit ? (
            <button
              type="button"
              onClick={onRemove}
              title="Textstil entfernen"
              className="ml-auto rounded p-1 text-zinc-400 hover:text-red-600"
            >
              <Trash2 className="size-3.5" />
            </button>
          ) : null}
        </div>
        <div
          className="overflow-hidden rounded-lg px-4 py-3 ring-1 ring-zinc-200"
          style={{ background }}
        >
          <p
            className="truncate"
            style={{
              fontFamily: `"${style.fontFamily}", Inter, sans-serif`,
              fontWeight: style.fontWeight,
              fontStyle: style.italic ? "italic" : undefined,
              fontSize: Math.min(56, Math.max(12, style.fontSize * 0.55)),
              lineHeight: style.lineHeight,
              letterSpacing: `${style.letterSpacing}em`,
              textTransform: style.uppercase ? "uppercase" : undefined,
              fontSynthesis: "none",
              color,
            }}
          >
            {style.key === "quote" ? "„Gute Gestaltung ist ehrlich.“" : "Sommerfest am Hafen 2026"}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 content-start gap-2">
        <div className="col-span-2">
          <FontPicker
            value={style.fontFamily}
            onChange={(family) =>
              onChange({
                fontFamily: family,
                fontWeight: closestWeight(family, style.fontWeight),
                italic: style.italic && hasItalic(family, closestWeight(family, style.fontWeight)),
              })
            }
          />
        </div>
        <Select
          className="h-8"
          value={style.fontWeight}
          disabled={!canEdit}
          onChange={(event) => {
            const weight = Number(event.target.value);
            onChange({
              fontWeight: weight,
              italic: style.italic && hasItalic(style.fontFamily, weight),
            });
          }}
        >
          {(font?.weights ?? [400, 700]).map((weight) => (
            <option key={weight} value={weight}>
              {WEIGHT_LABELS[weight] ?? weight}
            </option>
          ))}
        </Select>
        <NumberField
          label="Größe"
          value={style.fontSize}
          unit="px"
          min={1}
          max={1000}
          disabled={!canEdit}
          onChange={(fontSize) => onChange({ fontSize })}
        />
        <NumberField
          label="Zeile"
          value={style.lineHeight}
          precision={2}
          step={0.05}
          min={0.5}
          max={5}
          disabled={!canEdit}
          onChange={(lineHeight) => onChange({ lineHeight })}
        />
        <NumberField
          label="Abstand"
          value={Math.round(style.letterSpacing * 100)}
          unit="%"
          min={-50}
          max={200}
          disabled={!canEdit}
          onChange={(value) => onChange({ letterSpacing: value / 100 })}
        />
        <Select
          className="h-8"
          value={style.color ?? ""}
          disabled={!canEdit}
          onChange={(event) => onChange({ color: event.target.value || null })}
          aria-label="Farbe"
        >
          <option value="">Farbe: keine Vorgabe</option>
          {colors.map((entry) => (
            <option key={entry.key} value={entry.key}>
              Farbe: {entry.name}
            </option>
          ))}
        </Select>
        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={style.italic}
              disabled={!canEdit || !hasItalic(style.fontFamily, style.fontWeight)}
              onChange={(event) => onChange({ italic: event.target.checked })}
              className="size-4 accent-brand-600"
            />
            Kursiv
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={style.uppercase}
              onChange={(event) => onChange({ uppercase: event.target.checked })}
              className="size-4 accent-brand-600"
            />
            VERSALIEN
          </label>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------

function FontsSection({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { fonts } = useOrgFonts(orgId);
  const input = useRef<HTMLInputElement>(null);
  const [google, setGoogle] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const upload = useMutation(
    orpc.fonts.upload.mutationOptions({
      onSuccess: async (uploaded) => {
        await queryClient.invalidateQueries({ queryKey: orpc.fonts.key() });
        toast.success(
          uploaded.length === 1
            ? `„${uploaded[0]!.family}“ hinzugefügt`
            : `${uploaded.length} Schriften hinzugefügt`,
        );
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  return (
    <Card>
      <CardHeader
        title="Schriften"
        description="Eigene Schriften und Google Fonts. Sie liegen auf unserem Server (kein Laden von Google) und stehen allen Projekten der Organisation zur Verfügung."
        actions={
          canEdit ? (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setGoogle(true)}>
                <Download /> Google Font
              </Button>
              <Button
                size="sm"
                variant="secondary"
                loading={upload.isPending}
                onClick={() => input.current?.click()}
              >
                <Upload /> Hochladen
              </Button>
            </div>
          ) : null
        }
      />
      <input
        ref={input}
        type="file"
        multiple
        accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
        className="hidden"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          if (files.length > 0) upload.mutate({ orgId, files });
          event.target.value = "";
        }}
      />
      <div className="p-5">
        {fonts.length === 0 ? (
          <p className="text-sm text-zinc-500">
            Noch keine eigenen Schriften. Die 19 Standardschriften (Inter, Montserrat, Playfair
            Display …) stehen immer zur Verfügung.
          </p>
        ) : (
          <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
            {fonts.map((font) => (
              <li key={font.id} className="flex items-center gap-3 px-4 py-3">
                <Type className="size-4 shrink-0 text-zinc-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg" style={{ fontFamily: `"${font.family}"` }}>
                    {font.family}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {[...new Set(font.faces.map((face) => face.weight))]
                      .map((weight) => WEIGHT_LABELS[weight] ?? weight)
                      .join(", ")}
                    {font.faces.some((face) => face.style === "italic") ? " · mit Kursiv" : ""} ·{" "}
                    {formatBytes(font.sizeBytes)}
                  </p>
                </div>
                <Badge tone={font.source === "google" ? "neutral" : "brand"}>
                  {font.source === "google" ? "Google Fonts" : "Hochgeladen"}
                </Badge>
                {canEdit ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Schrift entfernen"
                    onClick={() => setRemoving(font.id)}
                  >
                    <Trash2 />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canEdit ? (
          <p className="mt-3 text-xs text-zinc-500">
            Mit dem Hochladen bestätigst du, dass die Lizenz der Schrift die Nutzung in Designs und
            Druckdaten erlaubt. Hochgeladene Schriften werden nur für diese Organisation
            ausgeliefert.
          </p>
        ) : null}
      </div>
      {google ? <GoogleFontsDialog orgId={orgId} onClose={() => setGoogle(false)} /> : null}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        danger
        title="Schrift entfernen?"
        description="Texte, die sie verwenden, werden danach in einer Ersatzschrift angezeigt, bis du eine andere Schrift wählst."
        confirmLabel="Entfernen"
        onConfirm={async () => {
          if (!removing) return;
          await client.fonts.remove({ orgId, fontId: removing });
          await queryClient.invalidateQueries({ queryKey: orpc.fonts.key() });
          setRemoving(null);
        }}
      />
    </Card>
  );
}

function GoogleFontsDialog({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(timer);
  }, [query]);
  const results = useQuery(
    orpc.fonts.googleSearch.queryOptions({ input: { orgId, query: debounced } }),
  );
  const [busy, setBusy] = useState<string | null>(null);
  const add = async (font: GoogleFont) => {
    setBusy(font.id);
    try {
      await client.fonts.importGoogle({ orgId, googleId: font.id });
      await queryClient.invalidateQueries({ queryKey: orpc.fonts.key() });
      toast.success(`„${font.family}“ hinzugefügt`);
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title="Google Font hinzufügen"
        description="Die Schriftdateien werden einmalig auf unseren Server kopiert. Browser laden sie danach nur von hier."
        className="max-w-xl"
      >
        <label className="mb-3 flex h-9 items-center gap-2 rounded-lg border border-zinc-300 px-3">
          <Search className="size-4 text-zinc-400" />
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="z. B. Work Sans, Fraunces, Outfit"
            className="w-full bg-transparent text-sm outline-none"
          />
        </label>
        <div className="max-h-[50vh] overflow-y-auto">
          {results.isLoading ? <p className="p-3 text-sm text-zinc-500">Suche …</p> : null}
          {results.isError ? (
            <p className="p-3 text-sm text-red-600">{errorMessage(results.error)}</p>
          ) : null}
          {results.data?.length === 0 ? (
            <p className="p-3 text-sm text-zinc-500">Keine Schrift gefunden.</p>
          ) : null}
          <ul className="divide-y divide-zinc-100">
            {results.data?.map((font) => (
              <li key={font.id} className="flex items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{font.family}</p>
                  <p className="text-xs text-zinc-500">
                    {font.category} · {font.weights.length}{" "}
                    {font.weights.length === 1 ? "Stärke" : "Stärken"}
                    {font.styles.includes("italic") ? " · Kursiv" : ""}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={busy === font.id}
                  disabled={busy !== null}
                  onClick={() => void add(font)}
                >
                  Hinzufügen
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Logos & graphics
// ---------------------------------------------------------------------------

function AssetThumb({ url, name }: { url: string; name: string }) {
  return (
    <div
      className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-lg border border-zinc-200 p-3"
      style={{ background: CHECKER }}
    >
      <img src={url} alt={name} className="max-h-full max-w-full object-contain" />
    </div>
  );
}

function LogosSection({ orgId, kit, canEdit }: { orgId: string; kit: BrandKit; canEdit: boolean }) {
  const mutation = useKitMutation(orgId, kit.id);
  const [picking, setPicking] = useState<BrandLogoVariant | null>(null);
  const save = (variant: BrandLogoVariant, asset: Asset | null) => {
    const logos = kit.logos
      .filter((logo) => logo.variant !== variant)
      .map((logo) => ({ variant: logo.variant, assetId: logo.asset.id }));
    if (asset) logos.push({ variant, assetId: asset.id });
    mutation.mutate({ orgId, kitId: kit.id, logos });
  };
  return (
    <Card>
      <CardHeader
        title="Logos"
        description="Varianten für helle und dunkle Hintergründe. Im Editor lassen sie sich mit einem Klick einfügen."
      />
      <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
        {BRAND_LOGO_VARIANTS.map((variant) => {
          const logo = kit.logos.find((entry) => entry.variant === variant);
          return (
            <div key={variant} className="space-y-2">
              <p className="text-sm font-medium text-zinc-700">{BRAND_LOGO_LABELS[variant]}</p>
              {logo ? (
                <AssetThumb
                  url={logo.asset.thumbnailUrl ?? logo.asset.url}
                  name={logo.asset.name}
                />
              ) : (
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setPicking(variant)}
                  className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-zinc-300 text-sm text-zinc-500 hover:border-brand-400 hover:text-brand-700 disabled:hover:border-zinc-300 disabled:hover:text-zinc-500"
                >
                  <ImagePlus className="size-5" />
                  {canEdit ? "Aus Mediathek wählen" : "Nicht hinterlegt"}
                </button>
              )}
              {logo && canEdit ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setPicking(variant)}>
                    Ändern
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => save(variant, null)}>
                    Entfernen
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <MediaPickerDialog
        orgId={orgId}
        open={picking !== null}
        onOpenChange={(open) => !open && setPicking(null)}
        title={picking ? BRAND_LOGO_LABELS[picking] : "Logo"}
        description="SVG ist ideal: Logos bleiben im Druck gestochen scharf."
        onPick={([asset]) => {
          if (picking && asset) save(picking, asset);
          setPicking(null);
        }}
      />
    </Card>
  );
}

function GraphicsSection({
  orgId,
  kit,
  canEdit,
}: {
  orgId: string;
  kit: BrandKit;
  canEdit: boolean;
}) {
  const mutation = useKitMutation(orgId, kit.id);
  const [picking, setPicking] = useState(false);
  const ids = kit.graphics.map((asset) => asset.id);
  return (
    <Card>
      <CardHeader
        title="Grafiken"
        description="Sticker, Muster, Icons und Fotos der Marke – im Editor unter „Marke“."
        actions={
          canEdit ? (
            <Button size="sm" variant="secondary" onClick={() => setPicking(true)}>
              <Plus /> Grafiken
            </Button>
          ) : null
        }
      />
      <div className="p-5">
        {kit.graphics.length === 0 ? (
          <p className="text-sm text-zinc-500">Noch keine Grafiken hinterlegt.</p>
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {kit.graphics.map((asset) => (
              <div key={asset.id} className="group relative">
                <AssetThumb url={asset.thumbnailUrl ?? asset.url} name={asset.name} />
                {canEdit ? (
                  <button
                    type="button"
                    title="Entfernen"
                    onClick={() =>
                      mutation.mutate({
                        orgId,
                        kitId: kit.id,
                        graphics: ids.filter((id) => id !== asset.id),
                      })
                    }
                    className="absolute top-1.5 right-1.5 rounded bg-white/90 p-1 text-zinc-600 opacity-0 shadow-sm transition group-hover:opacity-100 hover:text-red-600"
                  >
                    <X className="size-3.5" />
                  </button>
                ) : null}
                <p className="mt-1 truncate text-xs text-zinc-500">{asset.name}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      <MediaPickerDialog
        orgId={orgId}
        open={picking}
        onOpenChange={setPicking}
        multiple
        title="Grafiken hinzufügen"
        onPick={(assets) =>
          mutation.mutate({
            orgId,
            kitId: kit.id,
            graphics: [
              ...ids,
              ...assets.map((asset) => asset.id).filter((id) => !ids.includes(id)),
            ],
          })
        }
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Spacing, radii, print
// ---------------------------------------------------------------------------

function SpacingSection({ tokens, update }: { tokens: BrandTokens; update: Update }) {
  const radiusKeys = ["sm", "md", "lg"] as const;
  return (
    <Card>
      <CardHeader
        title="Abstände & Radien"
        description="Werte für ein 1080 px breites Format; in anderen Formaten proportional."
      />
      <div className="grid gap-6 p-5 md:grid-cols-2">
        <div className="space-y-2">
          <p className="text-sm font-medium text-zinc-700">Abstände</p>
          {BRAND_SPACING_KEYS.map((key) => (
            <div key={key} className="flex items-center gap-3">
              <span className="w-8 text-xs font-medium text-zinc-500 uppercase">{key}</span>
              <NumberField
                label=""
                value={tokens.spacing[key]}
                unit="px"
                min={0}
                max={1000}
                className="w-28"
                onChange={(value) =>
                  update((draft) => {
                    draft.spacing[key] = value;
                  })
                }
              />
              <span
                className="h-3 rounded-sm bg-brand-200"
                style={{ width: Math.min(240, tokens.spacing[key] * 1.5) }}
              />
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-zinc-700">Eckenradien</p>
          {radiusKeys.map((key) => (
            <div key={key} className="flex items-center gap-3">
              <span className="w-16 text-xs font-medium text-zinc-500">
                {BRAND_RADIUS_LABELS[key]}
              </span>
              <NumberField
                label=""
                value={tokens.radius[key]}
                unit="px"
                min={0}
                max={1000}
                className="w-28"
                onChange={(value) =>
                  update((draft) => {
                    draft.radius[key] = value;
                  })
                }
              />
              <span
                className="size-9 border-2 border-brand-400 bg-brand-50"
                style={{ borderRadius: Math.min(18, tokens.radius[key] / 2) }}
              />
            </div>
          ))}
          <p className="text-xs text-zinc-500">
            Dazu kommen immer „{BRAND_RADIUS_LABELS.none}“ (0) und „{BRAND_RADIUS_LABELS.full}“.
          </p>
        </div>
      </div>
    </Card>
  );
}

function PrintSection({ tokens, update }: { tokens: BrandTokens; update: Update }) {
  const profile = kitProfile(tokens);
  return (
    <Card>
      <CardHeader
        title="Druck"
        description="Standard für neue Flyer und Plakate in Projekten mit diesem Kit; pro Datei änderbar."
      />
      <div className="grid gap-4 p-5 md:grid-cols-3">
        <Field label="Druckerei">
          <Select
            value={tokens.print.provider ?? ""}
            onChange={(event) =>
              update((draft) => {
                draft.print.provider = event.target.value || null;
              })
            }
          >
            <option value="">Keine Vorgabe (SAXOPRINT)</option>
            {PRINT_PROVIDERS.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Papier">
          <Select
            value={tokens.print.paper ?? ""}
            onChange={(event) =>
              update((draft) => {
                draft.print.paper = (event.target.value || null) as BrandTokens["print"]["paper"];
              })
            }
          >
            <option value="">Keine Vorgabe (gestrichen)</option>
            <option value="coated">Gestrichen (Bilderdruck)</option>
            <option value="uncoated">Ungestrichen (Offset, Natur, Recycling)</option>
          </Select>
        </Field>
        <Field label="Farbprofil" hint={`Aktuell: ${profile.label}`}>
          <Select
            value={tokens.print.profile ?? ""}
            onChange={(event) =>
              update((draft) => {
                draft.print.profile = event.target.value || null;
              })
            }
          >
            <option value="">Automatisch (Druckerei & Papier)</option>
            {PRINT_PROFILES.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Card>
  );
}
